import React from 'react';
import {WithPermission} from 'admin-on-rest';
import {resolveAccess} from '../shared/authFunctions';
import {ViewTitle} from 'admin-on-rest/lib/mui';
import {Card, CardHeader, CardText, CardActions} from 'material-ui/Card';
import LocationsStatus from '../components/LocationsStatus';
import FlatButton from 'material-ui/FlatButton';
import Avatar from 'material-ui/Avatar';
import ListIcon from 'material-ui/svg-icons/action/view-list';
import AddIcon from 'material-ui/svg-icons/content/add';
import StoreIcon from 'material-ui/svg-icons/action/store';
import {fuseLocationIcon as LocationIcon} from './location';
import {fuseDbaIcon as DbaIcon} from './dba';
import {fuseCompanyIcon as CompanyIcon} from './company';
import {fuseLocationTypeIcon as LocationTypeIcon} from './type';
import {fuseDistrictIcon as DistrictIcon} from './district';
import {fuseLocationPriceLevelIcon as PriceLevelIcon} from './priceLevel';
import {fuseLocationAdditionalTaxIcon as AdditionalTaxesIcon} from './additionalTax';
import {dashStyles} from '../shared/styles-dashboard';

export default () => (
    <div>
        {/* Locations Full Access */}
        <WithPermission resolve={(props) => resolveAccess(props.permissions, ['locations'])}>
            <Card style={{...dashStyles.marginBottom}}>
                <StoreIcon style={dashStyles.dashIcon}/>
                <ViewTitle title="Locations"/>
                <CardText>
                    <p>
                        Create and manage all of your Locations (Stores) for your Business. Here you can
                        create, view and edit physical Locations, DBAs, Companies, Location Types, Districts,
                        Price Levels, and Additional Taxes.
                    </p>
                    <img src="img-diagram-Location-Dashboard.png" alt="Diagram of Location Tree"
                         style={dashStyles.diagram}/>
                </CardText>
            </Card>
        </WithPermission>

        {/* Locations View Only Access*/}
        <WithPermission resolve={(props) => resolveAccess(props.permissions, [], ['locations_vo'])}>
            <Card style={{...dashStyles.marginBottom}}>
                <StoreIcon style={dashStyles.dashIcon}/>
                <ViewTitle title="Locations"/>
                <CardText>
                    <p>
                        View all of your Locations (Stores) for your Business. Here you can view physical
                        Locations, DBAs, Companies, Location Types, Districts, Price Levels, and
                        Additional Taxes.
                    </p>
                    <img src="img-diagram-Location-Dashboard.png" alt="Diagram of Location Tree"
                         style={dashStyles.diagram}/>
                </CardText>
            </Card>
        </WithPermission>

        {/* Locations Status */}
        <WithPermission resolve={(props) => resolveAccess(props.permissions, ['locations', 'locations_vo'])}>
            <LocationsStatus/>
        </WithPermission>

        {/* Locations */}
        <WithPermission resolve={(props) => resolveAccess(props.permissions, ['locations'])}>
            <Card style={{...dashStyles.listCard, ...dashStyles.marginBottom}} className="listCard">
                <CardHeader
                    title="Physical Locations"
                    subtitle="Store / Location details. Click to read more."
                    avatar={<Avatar icon={<LocationIcon/>} style={dashStyles.avatar}/>}
                    actAsExpander={true}
                    showExpandableButton={true}
                />
                <CardText expandable={true}>
                    <p>
                        A <strong>Physical Location</strong> holds your Stores' information and Payment Gateway details
                        so the POS System can begin transactions. The POS System uses the Location ID to login to your
                        Physical Location.
                    </p>
                    <img src="img-diagram-Location-List.png" alt="Diagram of Locations" style={dashStyles.diagram}/>
                    <p>
                        A Location requires several elements to exist before being created. Before creating a Location,
                        you must create at least one of each:&nbsp;
                        <a href="#/ProductTypes/create" style={dashStyles.fieldLink}>Product Type</a>,&nbsp;&nbsp;
                        <a href="#/DBAs/create" style={dashStyles.fieldLink}>DBA</a>,&nbsp;&nbsp;
                        <a href="#/Companies/create" style={dashStyles.fieldLink}>Company</a>,&nbsp;&nbsp;
                        <a href="#/LocationTypes/create" style={dashStyles.fieldLink}>Location Type</a>,&nbsp;&nbsp;
                        <a href="#/Districts/create" style={dashStyles.fieldLink}>District</a>, &nbsp;and&nbsp;&nbsp;
                        <a href="#/PriceLevels/create" style={dashStyles.fieldLink}>Price Level</a>.
                    </p>
                </CardText>
                <CardActions>
                    <FlatButton icon={<ListIcon/>} label="List Locations" href="#/Locations"/>
                    <FlatButton icon={<AddIcon/>} label="Add a Location" href="#/Locations/create"/>
                </CardActions>
            </Card>
        </WithPermission>

        {/* Locations View Only */}
        <WithPermission resolve={(props) => resolveAccess(props.permissions, [], ['locations_vo'])}>
            <Card style={{...dashStyles.listCard, ...dashStyles.marginBottom}} className="listCard">
                <CardHeader
                    title="Physical Locations"
                    subtitle="Store / Location details. Click to read more."
                    avatar={<Avatar icon={<LocationIcon/>} style={dashStyles.avatar}/>}
                    actAsExpander={true}
                    showExpandableButton={true}
                />
                <CardText expandable={true}>
                    <p>
                        A <strong>Physical Location</strong> holds your Stores' information and Payment Gateway details
                        so the POS System can begin transactions. The POS System uses the Location ID to login to your
                        Physical Location.
                    </p>
                    <img src="img-diagram-Location-List.png" alt="Diagram of Locations" style={dashStyles.diagram}/>
                </CardText>
                <CardActions>
                    <FlatButton icon={<ListIcon/>} label="List Locations" href="#/Locations"/>
                </CardActions>
            </Card>
        </WithPermission>

        {/* DBAs */}
        <WithPermission resolve={(props) => resolveAccess(props.permissions, ['locations', 'locations_vo'])}>
            <Card style={{...dashStyles.listCard, ...dashStyles.marginBottom}} className="listCard">
                <CardHeader
                    title="DBA (Doing Business As)"
                    subtitle="Locations use DBAs to display the correct Product Types on the POS System. Click to read more."
                    avatar={<Avatar icon={<DbaIcon/>} style={dashStyles.avatar}/>}
                    actAsExpander={true}
                    showExpandableButton={true}
                />
                <CardText expandable={true}>
                    <p>
                        A <strong>DBA</strong> holds the Business Name and Product Type, and is used for Locations to
                        display products of that Product Type on the POS System.
                    </p>
                    <img src="img-diagram-DBA-List.png" alt="Diagram of Locations - DBAs" style={dashStyles.diagram}/>
                    <p>
                        There are instances in the Seasonal Retail Industry where you might have one DBA for a Fireworks
                        business and a DBA for a Halloween Costume Business. Instead of managing all Locations under the
                        same DBA, you can have multiple DBAs with different Product Types so employees will be able to
                        sell the correct Products in your Stores.
                    </p>
                </CardText>
                {/* Buttons For DBAs Full Access */}
                <WithPermission resolve={(props) => resolveAccess(props.permissions, ['locations'])}>
                    <CardActions>
                        <FlatButton icon={<ListIcon/>} label="List DBAs" href="#/DBAs"/>
                        <FlatButton icon={<AddIcon/>} label="Add a DBA" href="#/DBAs/create"/>
                    </CardActions>
                </WithPermission>
                {/* Buttons For DBAs View Only Access */}
                <WithPermission resolve={(props) => resolveAccess(props.permissions, [], ['locations_vo'])}>
                    <CardActions>
                        <FlatButton icon={<ListIcon/>} label="List DBAs" href="#/DBAs"/>
                    </CardActions>
                </WithPermission>
            </Card>
        </WithPermission>

        {/* Companies */}
        <WithPermission resolve={(props) => resolveAccess(props.permissions, ['locations', 'locations_vo'])}>
            <Card style={{...dashStyles.listCard, ...dashStyles.marginBottom}} className="listCard">
                <CardHeader
                    title="Companies"
                    subtitle="Each store (physical location) is associated with a Company. Click to read more."
                    avatar={<Avatar icon={<CompanyIcon/>} style={dashStyles.avatar}/>}
                    actAsExpander={true}
                    showExpandableButton={true}
                />
                <CardText expandable={true}>
                    <p>
                        A <strong>Company</strong> holds the Company name and your business address.
                    </p>
                    <img src="img-diagram-Company-List.png" alt="Diagram of Locations - Companies"
                         style={dashStyles.diagram}/>
                </CardText>
                {/* Buttons For Companies Full Access */}
                <WithPermission resolve={(props) => resolveAccess(props.permissions, ['locations'])}>
                    <CardActions>
                        <FlatButton icon={<ListIcon/>} label="List Companies" href="#/Companies"/>
                        <FlatButton icon={<AddIcon/>} label="Add a Company" href="#/Companies/create"/>
                    </CardActions>
                </WithPermission>
                {/* Buttons For Companies View Only Access */}
                <WithPermission resolve={(props) => resolveAccess(props.permissions, [], ['locations_vo'])}>
                    <CardActions>
                        <FlatButton icon={<ListIcon/>} label="List Companies" href="#/Companies"/>
                    </CardActions>
                </WithPermission>

            </Card>
        </WithPermission>

        {/* Location Types */}
        <WithPermission resolve={(props) => resolveAccess(props.permissions, ['locations', 'locations_vo'])}>
            <Card style={{...dashStyles.listCard, ...dashStyles.marginBottom}} className="listCard">
                <CardHeader
                    title="Location Types"
                    subtitle="Types of Locations for stores (warehouse, brick & mortar, stand, etc.). Click to read more."
                    avatar={<Avatar icon={<LocationTypeIcon/>} style={dashStyles.avatar}/>}
                    actAsExpander={true}
                    showExpandableButton={true}
                />
                <CardText expandable={true}>
                    <p>
                        A <strong>Location Type</strong> describes what type of Location each of your stores is. Your
                        stores may be in different types of locations, such as a brick & mortar, strip mall, tent,
                        stand,
                        warehouse, booth, etc.
                    </p>
                    <img src="img-diagram-Location-Type-List.png" alt="Diagram of Locations - Location Types"
                         style={dashStyles.diagram}/>
                </CardText>
                {/* Buttons For Location Types Full Access */}
                <WithPermission resolve={(props) => resolveAccess(props.permissions, ['locations'])}>
                    <CardActions>
                        <FlatButton icon={<ListIcon/>} label="List Location Types" href="#/LocationTypes"/>
                        <FlatButton icon={<AddIcon/>} label="Add a Location Type" href="#/LocationTypes/create"/>
                    </CardActions>
                </WithPermission>
                {/* Buttons For Location Types View Only Access */}
                <WithPermission resolve={(props) => resolveAccess(props.permissions, [], ['locations_vo'])}>
                    <CardActions>
                        <FlatButton icon={<ListIcon/>} label="List Location Types" href="#/LocationTypes"/>
                    </CardActions>
                </WithPermission>
            </Card>
        </WithPermission>

        {/* Districts */}
        <WithPermission resolve={(props) => resolveAccess(props.permissions, ['locations', 'locations_vo'])}>
            <Card style={{...dashStyles.listCard, ...dashStyles.marginBottom}} className="listCard">
                <CardHeader
                    title="Districts"
                    subtitle="Collections of Locations, tied to a geographical location or district manager. Click to read more."
                    avatar={<Avatar icon={<DistrictIcon/>} style={dashStyles.avatar}/>}
                    actAsExpander={true}
                    showExpandableButton={true}
                />
                <CardText expandable={true}>
                    <p>
                        A <strong>District</strong> groups Physical Locations to a geographical location or to a
                        District
                        Manager. Districts are used for Sale and Inventory Reporting to keep track of Districts'
                        progress.
                    </p>
                    <img src="img-diagram-District-List.png" alt="Diagram of Locations - Districts"
                         style={dashStyles.diagram}/>
                </CardText>
                {/* Buttons For Districts Full Access */}
                <WithPermission resolve={(props) => resolveAccess(props.permissions, ['locations'])}>
                    <CardActions>
                        <FlatButton icon={<ListIcon/>} label="List Districts" href="#/Districts"/>
                        <FlatButton icon={<AddIcon/>} label="Add a District" href="#/Districts/create"/>
                    </CardActions>
                </WithPermission>
                {/* Buttons For Districts View Only Access */}
                <WithPermission resolve={(props) => resolveAccess(props.permissions, [], ['locations_vo'])}>
                    <CardActions>
                        <FlatButton icon={<ListIcon/>} label="List Districts" href="#/Districts"/>
                    </CardActions>
                </WithPermission>
            </Card>
        </WithPermission>

        {/* Price Levels */}
        <WithPermission resolve={(props) => resolveAccess(props.permissions, ['locations', 'locations_vo'])}>
            <Card style={{...dashStyles.listCard, ...dashStyles.marginBottom}} className="listCard">
                <CardHeader
                    title="Price Levels"
                    subtitle="Named levels used for unique product pricing of specific Physical Locations or Locations grouped by DBA. Click to read more."
                    avatar={<Avatar icon={<PriceLevelIcon/>} style={dashStyles.avatar}/>}
                    actAsExpander={true}
                    showExpandableButton={true}
                />
                <CardText expandable={true}>
                    <p>
                        A <strong>Price Level</strong> is used to label a <em>Price Level Price</em> of a Product.
                        Certain
                        Locations or DBAs could have different pricing of products, so Products can have Price Level
                        Prices
                        based on a certain Price Level.
                    </p>
                    <ul>
                        <li>If a Location or a DBA has a Price Level, the POS System will display the Price Level Price
                            of
                            a Product instead of its default price.
                        </li>
                        <li>If a Location has a Price Level but a Product doesn't have a Price Level Price, then the
                            Product's default price will display on the POS System.
                        </li>
                    </ul>
                    <img src="img-diagram-Price-Level-List.png" alt="Diagram of Locations - Price Levels"
                         style={dashStyles.diagram}/>
                </CardText>
                {/* Button For Price Level Full Access */}
                <WithPermission resolve={(props) => resolveAccess(props.permissions, ['locations'])}>
                    <CardActions>
                        <FlatButton icon={<ListIcon/>} label="List Price Levels" href="#/PriceLevels"/>
                        <FlatButton icon={<AddIcon/>} label="Add a Price Level" href="#/PriceLevels/create"/>
                    </CardActions>
                </WithPermission>
                {/* Button For Price Level View Only Access */}
                <WithPermission resolve={(props) => resolveAccess(props.permissions, [], ['locations_vo'])}>
                    <CardActions>
                        <FlatButton icon={<ListIcon/>} label="List Price Levels" href="#/PriceLevels"/>
                    </CardActions>
                </WithPermission>
            </Card>
        </WithPermission>

        {/* Additional Taxes */}
        <WithPermission resolve={(props) => resolveAccess(props.permissions, ['locations', 'locations_vo'])}>
            <Card style={{...dashStyles.listCard, ...dashStyles.marginBottom}} className="listCard">
                <CardHeader
                    title="Additional Taxes"
                    subtitle="Some DBAs and Locations require additional taxes per state law."
                    avatar={<Avatar icon={<AdditionalTaxesIcon/>} style={dashStyles.avatar}/>}
                    actAsExpander={false}
                    showExpandableButton={false}
                />
                {/* Buttons For Additional Taxes Full Access */}
                <WithPermission resolve={(props) => resolveAccess(props.permissions, ['locations'])}>
                    <CardActions>
                        <FlatButton icon={<ListIcon/>} label="List Additional Taxes" href="#/AdditionalTaxes"/>
                        <FlatButton icon={<AddIcon/>} label="Add an Additional Tax" href="#/AdditionalTaxes/create"/>
                    </CardActions>
                </WithPermission>
                {/* Buttons For Additional Taxes View Only Access */}
                <WithPermission resolve={(props) => resolveAccess(props.permissions, [], ['locations_vo'])}>
                    <CardActions>
                        <FlatButton icon={<ListIcon/>} label="List Additional Taxes" href="#/AdditionalTaxes"/>
                    </CardActions>
                </WithPermission>

            </Card>
        </WithPermission>
    </div>
);



// WEBPACK FOOTER //
// ./src/Fuse/Location/dashboard.js